import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { NaviGate } from "../../../Utility/function";
import { getShareHolders, createEquityEntry, getEquityEntries } from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";

const Equity = () => {
  const { shopItem, UserItem, showLoader, hideLoader } = useContext(WarehouseContext);

  // Form state
  const [formData, setFormData] = useState({
    shareholder_id: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    remarks: "",
  });

  // Data
  const [shareholders, setShareholders] = useState([]);
  const [equityList, setEquityList] = useState([]);
  const [bankBalance, setBankBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filter for history
  const [filterSH, setFilterSH] = useState("");

  useEffect(() => {
    fetchShareholders();
    fetchEquityList();
  }, []);

  const fetchShareholders = async () => {
    try {
      const res = await getShareHolders({ shop_id: shopItem?.shop_id || "" });
      setShareholders(res?.data?.data || []);
    } catch {
      toast.error("Failed to load shareholders");
    }
  };

  const fetchEquityList = async () => {
    showLoader?.();
    try {
      const params = { shop_id: shopItem?.shop_id || "" };
      const res = await getEquityEntries(params);
      if (res?.data?.status === "success") {
        setEquityList(res.data.data || []);
        setBankBalance(res.data.bank_balance ?? null);
      }
    } catch {
      toast.error("Failed to load equity history");
    } finally {
      hideLoader?.();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shareholder_id) {
      toast.error("Please select a shareholder");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (bankBalance !== null && parseFloat(formData.amount) > bankBalance) {
      toast.error(
        `Amount cannot exceed bank balance (₹ ${bankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        shop_id: shopItem?.shop_id || "",
        created_by: String(UserItem?.id || ""),
      };

      const res = await createEquityEntry(payload);
      if (res?.data?.status === "success") {
        toast.success("Equity entry recorded successfully");
        setFormData({
          shareholder_id: "",
          date: new Date().toISOString().split("T")[0],
          amount: "",
          remarks: "",
        });
        fetchEquityList();
      } else {
        toast.error(res?.data?.message || "Failed to save equity entry");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save equity entry";
      const available = err?.response?.data?.available_balance;
      if (available !== undefined) {
        toast.error(`${msg}. Available: ₹ ${available}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered list
  const filteredList = filterSH
    ? equityList.filter((e) => String(e.shareholder_id) === String(filterSH))
    : equityList;

  // Total equity withdrawn
  const totalEquity = filteredList.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  return (
    <div className="page-content py-1">
      <NaviGate />
      <div className="mx-2">
        {/* ─── Page Header ─── */}
        <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
          <div>
            <h5 className="fw-bold mb-0">
              <i className="bi bi-pie-chart-fill me-2 text-primary" />
              Equity Management
            </h5>
            <small className="text-muted">Record equity withdrawals for shareholders</small>
          </div>

          {/* Bank Balance Badge */}
          {bankBalance !== null && (
            <div
              className="px-3 py-2 rounded-3 text-white fw-semibold"
              style={{
                background: bankBalance > 0
                  ? "linear-gradient(135deg, #10b981, #059669)"
                  : "linear-gradient(135deg, #ef4444, #b91c1c)",
                fontSize: "14px",
              }}
            >
              <i className="bi bi-bank me-1" />
              Bank Balance: ₹{" "}
              {bankBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        <div className="row g-3">
          {/* ─── Form Card ─── */}
          <div className="col-md-4">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header text-white border-0"
                style={{
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-plus-circle me-2" />
                  New Equity Entry
                </h6>
              </div>

              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  {/* Shareholder */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">
                      Shareholder <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="shareholder_id"
                      value={formData.shareholder_id}
                      onChange={handleChange}
                      required
                      id="equity-shareholder-select"
                    >
                      <option value="">— Select Shareholder —</option>
                      {shareholders.map((sh) => (
                        <option key={sh.id} value={sh.id}>
                          {sh.name}
                          {sh.equity ? ` (${sh.equity}%)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">
                      Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      id="equity-date-input"
                    />
                  </div>

                  {/* Amount */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">
                      Amount (₹) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      min="0.01"
                      step="0.01"
                      required
                      id="equity-amount-input"
                    />
                    {bankBalance !== null && formData.amount && (
                      <div className="mt-1">
                        {parseFloat(formData.amount) > bankBalance ? (
                          <small className="text-danger">
                            <i className="bi bi-exclamation-triangle-fill me-1" />
                            Exceeds bank balance of ₹{" "}
                            {bankBalance.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </small>
                        ) : (
                          <small className="text-success">
                            <i className="bi bi-check-circle-fill me-1" />
                            Remaining balance after: ₹{" "}
                            {(bankBalance - parseFloat(formData.amount || 0)).toLocaleString(
                              undefined,
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                            )}
                          </small>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Remark */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small">Remark</label>
                    <textarea
                      className="form-control"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Optional notes..."
                      id="equity-remarks-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn w-100 fw-semibold text-white"
                    disabled={submitting}
                    style={{
                      background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                      borderRadius: "8px",
                    }}
                    id="equity-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2" />
                        Save Equity Entry
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* ─── History Table ─── */}
          <div className="col-md-8">
            <div
              className="card border-0 shadow-sm"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
                style={{
                  background: "linear-gradient(135deg, #f8fafc, #e2e8f0)",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <h6 className="mb-0 fw-bold text-dark">
                  <i className="bi bi-clock-history me-2 text-indigo-600" />
                  Equity Withdrawal History
                </h6>

                {/* Filter by shareholder */}
                <div style={{ minWidth: "200px" }}>
                  <select
                    className="form-select form-select-sm"
                    value={filterSH}
                    onChange={(e) => setFilterSH(e.target.value)}
                    id="equity-filter-sh"
                  >
                    <option value="">All Shareholders</option>
                    {shareholders.map((sh) => (
                      <option key={sh.id} value={sh.id}>
                        {sh.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Summary row */}
              <div className="px-3 pt-3">
                <div className="row g-2 mb-3">
                  <div className="col-sm-6">
                    <div
                      className="rounded-3 p-3 text-white"
                      style={{
                        background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                      }}
                    >
                      <p className="mb-0" style={{ fontSize: "11px", opacity: 0.85 }}>
                        TOTAL EQUITY WITHDRAWN
                      </p>
                      <h4 className="mb-0 fw-bold">
                        ₹{" "}
                        {totalEquity.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </h4>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div
                      className="rounded-3 p-3 text-white"
                      style={{
                        background: "linear-gradient(135deg, #10b981, #059669)",
                      }}
                    >
                      <p className="mb-0" style={{ fontSize: "11px", opacity: 0.85 }}>
                        CURRENT BANK BALANCE
                      </p>
                      <h4 className="mb-0 fw-bold">
                        {bankBalance !== null
                          ? `₹ ${bankBalance.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="px-3 py-3" style={{ width: "50px" }}>#</th>
                        <th className="px-3 py-3">Shareholder</th>
                        <th className="px-3 py-3">Date</th>
                        <th className="px-3 py-3 text-end">Amount (₹)</th>
                        <th className="px-3 py-3">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredList.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-3 d-block mb-2 text-secondary" />
                            No equity withdrawals found
                          </td>
                        </tr>
                      ) : (
                        filteredList.map((entry, idx) => (
                          <tr key={entry.id}>
                            <td className="px-3 py-2 text-muted">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <span
                                className="badge rounded-pill px-3 py-2"
                                style={{
                                  background: "rgba(99,102,241,0.12)",
                                  color: "#4f46e5",
                                  fontWeight: 600,
                                }}
                              >
                                <i className="bi bi-person-fill me-1" />
                                {entry.shareholder_name}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-secondary">{entry.date}</td>
                            <td className="px-3 py-2 text-end">
                              <span className="fw-bold text-danger">
                                ₹{" "}
                                {parseFloat(entry.amount).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-muted">
                              {entry.remarks || "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {filteredList.length > 0 && (
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan={3} className="px-3 py-2 fw-bold text-end">
                            Total
                          </td>
                          <td className="px-3 py-2 fw-bold text-end text-danger">
                            ₹{" "}
                            {totalEquity.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Equity;
